import type { OrchestrationThreadActivity, ProviderDriverKind } from "@afar-research/contracts";
import { codexSubagentSpawnBatchId } from "@afar-research/shared/codexSubagentSpawn";

import { compareOrchestrationActivitiesByOrder } from "./orchestrationActivityOrder.ts";

export const MAX_CODEX_SUBAGENTS = 128;
export const MAX_CODEX_SUBAGENT_ACTIVITIES = 16;
export const MAX_CODEX_SUBAGENT_MESSAGES = 128;
export const MAX_CODEX_SUBAGENT_SOURCE_ACTIVITIES = 2_048;

export type CodexSubagentStatus =
  | "pending"
  | "running"
  | "waiting"
  | "idle"
  | "completed"
  | "failed"
  | "cancelled"
  | "interrupted";

export interface CodexSubagentActivity {
  readonly id: string;
  readonly createdAt: string;
  readonly tool: string;
  readonly label: string;
}

export interface CodexSubagentMessage {
  readonly id: string;
  readonly createdAt: string;
  readonly role: "user" | "assistant";
  readonly text: string;
}

export interface CodexSubagent {
  readonly id: string;
  readonly parentId: string | null;
  readonly spawnBatchId: string;
  readonly spawnOrder: number;
  readonly status: CodexSubagentStatus;
  readonly identity: string;
  readonly role?: string;
  readonly model?: string;
  readonly effort?: string;
  readonly currentActivity?: string;
  readonly lastTool?: string;
  readonly startedAt?: string;
  readonly completedAt?: string;
  readonly usage?: unknown;
  readonly result?: string;
  readonly error?: string;
  readonly messages: ReadonlyArray<CodexSubagentMessage>;
  readonly activities: ReadonlyArray<CodexSubagentActivity>;
}

export interface CodexSubagentSpawnBatch {
  readonly id: string;
  readonly createdAt: string;
  readonly agentIds: ReadonlyArray<string>;
}

export interface CodexSubagentProjection {
  readonly agents: ReadonlyArray<CodexSubagent>;
  readonly batches: ReadonlyArray<CodexSubagentSpawnBatch>;
  readonly liveCount: number;
  readonly totalCount: number;
  readonly truncated: boolean;
}

export type CodexCollabTool = "spawnAgent" | "sendInput" | "resumeAgent" | "wait" | "closeAgent";

export interface CodexCollabToolItem {
  readonly id: string;
  readonly tool: CodexCollabTool;
  readonly senderThreadId: string;
  readonly receiverThreadIds: ReadonlyArray<string>;
  readonly agentsStates: Readonly<
    Record<string, { readonly status?: string; readonly message?: string }>
  >;
  readonly status?: string;
  readonly model?: string;
  readonly reasoningEffort?: string;
  readonly prompt?: string;
}

export interface CodexSubagentActivityItem {
  readonly id: string;
  readonly parentThreadId?: string;
  readonly agentThreadId: string;
  readonly agentPath: string;
  readonly kind: "started" | "interacted" | "interrupted";
}

export interface CodexSubagentUsageItem {
  readonly agentThreadId: string;
  readonly usage: unknown;
}

interface CodexSubagentToolActivityItem {
  readonly id: string;
  readonly agentThreadId: string;
  readonly tool: string;
  readonly label: string;
}

interface CodexSubagentLifecycleItem {
  readonly agentThreadId: string;
  readonly status: CodexSubagentStatus;
  readonly error?: string;
}

interface CodexSubagentMessageItem {
  readonly agentThreadId: string;
  readonly text?: string;
}

interface MutableAgent {
  id: string;
  parentNativeThreadId: string;
  spawnBatchId: string;
  spawnOrder: number;
  status: CodexSubagentStatus;
  identity: string;
  role?: string;
  model?: string;
  effort?: string;
  currentActivity?: string;
  lastTool?: string;
  startedAt?: string;
  completedAt?: string;
  usage?: unknown;
  result?: string;
  error?: string;
  messages: CodexSubagentMessage[];
  activities: CodexSubagentActivity[];
}

type NativeProjectionEvent =
  | {
      readonly kind: "collab";
      readonly activity: OrchestrationThreadActivity;
      readonly item: CodexCollabToolItem;
    }
  | {
      readonly kind: "subagentActivity";
      readonly activity: OrchestrationThreadActivity;
      readonly item: CodexSubagentActivityItem;
    }
  | {
      readonly kind: "usage";
      readonly activity: OrchestrationThreadActivity;
      readonly item: CodexSubagentUsageItem;
    }
  | {
      readonly kind: "toolActivity";
      readonly activity: OrchestrationThreadActivity;
      readonly item: CodexSubagentToolActivityItem;
    }
  | {
      readonly kind: "lifecycle";
      readonly activity: OrchestrationThreadActivity;
      readonly item: CodexSubagentLifecycleItem;
    }
  | {
      readonly kind: "message";
      readonly activity: OrchestrationThreadActivity;
      readonly item: CodexSubagentMessageItem;
    };

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function trimmedString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/** Parse the persisted native Codex collaboration envelope shared by roster and timeline views. */
export function parseCodexCollabToolItem(payloadValue: unknown): CodexCollabToolItem | null {
  const payload = asRecord(payloadValue);
  if (payload?.itemType !== "collab_agent_tool_call") return null;
  const data = asRecord(payload.data);
  const item = asRecord(data?.item ?? data);
  if (item?.type !== "collabAgentToolCall") return null;
  const id = trimmedString(item.id);
  const senderThreadId = trimmedString(item.senderThreadId);
  const tool = item.tool;
  if (
    !id ||
    !senderThreadId ||
    (tool !== "spawnAgent" &&
      tool !== "sendInput" &&
      tool !== "resumeAgent" &&
      tool !== "wait" &&
      tool !== "closeAgent")
  ) {
    return null;
  }
  const receiverThreadIds = Array.isArray(item.receiverThreadIds)
    ? item.receiverThreadIds.flatMap((value) => {
        const id = trimmedString(value);
        return id ? [id] : [];
      })
    : [];
  const rawStates = asRecord(item.agentsStates);
  const agentsStates = Object.fromEntries(
    Object.entries(rawStates ?? {}).map(([agentId, state]) => {
      const stateRecord = asRecord(state);
      const status = trimmedString(stateRecord?.status);
      const message = trimmedString(stateRecord?.message);
      return [agentId, { ...(status ? { status } : {}), ...(message ? { message } : {}) }];
    }),
  );
  const status = trimmedString(item.status);
  const model = trimmedString(item.model);
  const reasoningEffort = trimmedString(item.reasoningEffort);
  const prompt = trimmedString(item.prompt);
  return {
    id,
    tool,
    senderThreadId,
    receiverThreadIds,
    agentsStates,
    ...(status ? { status } : {}),
    ...(model ? { model } : {}),
    ...(reasoningEffort ? { reasoningEffort } : {}),
    ...(prompt ? { prompt } : {}),
  };
}

/** Parse Codex's native retained subagent activity item. */
export function parseCodexSubagentActivityItem(
  payloadValue: unknown,
): CodexSubagentActivityItem | null {
  const payload = asRecord(payloadValue);
  if (payload?.itemType !== "subagent_activity") return null;
  const data = asRecord(payload.data);
  const item = asRecord(data?.item ?? data);
  if (item?.type !== "subAgentActivity") return null;
  const id = trimmedString(item.id);
  const parentThreadId = trimmedString(data?.threadId);
  const agentThreadId = trimmedString(item.agentThreadId);
  const agentPath = trimmedString(item.agentPath);
  const kind = item.kind;
  if (
    !id ||
    !agentThreadId ||
    !agentPath ||
    (kind !== "started" && kind !== "interacted" && kind !== "interrupted")
  ) {
    return null;
  }
  return {
    id,
    ...(parentThreadId ? { parentThreadId } : {}),
    agentThreadId,
    agentPath,
    kind,
  };
}

export function parseCodexSubagentUsageItem(
  activity: OrchestrationThreadActivity,
): CodexSubagentUsageItem | null {
  if (activity.kind !== "context-window.updated") return null;
  const payload = asRecord(activity.payload);
  const agentThreadId = trimmedString(payload?.childThreadId);
  return agentThreadId ? { agentThreadId, usage: activity.payload } : null;
}

function parseCodexSubagentToolActivityItem(
  activity: OrchestrationThreadActivity,
): CodexSubagentToolActivityItem | null {
  if (!activity.kind.startsWith("tool.")) return null;
  const payload = asRecord(activity.payload);
  const data = asRecord(payload?.data);
  const agentThreadId = trimmedString(data?.threadId);
  const itemType = trimmedString(payload?.itemType);
  if (!agentThreadId || !itemType || itemType === "collab_agent_tool_call") return null;
  const item = asRecord(data?.item);
  const id = trimmedString(item?.id) ?? activity.id;
  const title = trimmedString(payload?.title);
  const detail = trimmedString(payload?.detail);
  const nativeTool = trimmedString(item?.tool);
  const tool = nativeTool ?? title ?? itemType;
  return { id, agentThreadId, tool, label: detail ?? title ?? activity.summary };
}

function isCodexSubagentStatus(value: unknown): value is CodexSubagentStatus {
  return (
    value === "pending" ||
    value === "running" ||
    value === "waiting" ||
    value === "idle" ||
    value === "completed" ||
    value === "failed" ||
    value === "cancelled" ||
    value === "interrupted"
  );
}

function parseCodexSubagentLifecycleItem(
  activity: OrchestrationThreadActivity,
): CodexSubagentLifecycleItem | null {
  if (activity.kind !== "subagent.lifecycle") return null;
  const payload = asRecord(activity.payload);
  const agentThreadId = trimmedString(payload?.agentThreadId);
  const status = payload?.status;
  if (!agentThreadId || !isCodexSubagentStatus(status)) return null;
  const error = trimmedString(payload?.error);
  return { agentThreadId, status, ...(error ? { error } : {}) };
}

function parseCodexSubagentMessageItem(
  activity: OrchestrationThreadActivity,
): CodexSubagentMessageItem | null {
  if (activity.kind !== "subagent.message.completed") return null;
  const payload = asRecord(activity.payload);
  const agentThreadId = trimmedString(payload?.agentThreadId);
  if (!agentThreadId) return null;
  const text = trimmedString(payload?.text);
  return { agentThreadId, ...(text ? { text } : {}) };
}

function parseNativeProjectionEvent(
  activity: OrchestrationThreadActivity,
): NativeProjectionEvent | null {
  const collab = parseCodexCollabToolItem(activity.payload);
  if (collab) return { kind: "collab", activity, item: collab };
  const subagentActivity = parseCodexSubagentActivityItem(activity.payload);
  if (subagentActivity) {
    return { kind: "subagentActivity", activity, item: subagentActivity };
  }
  const usage = parseCodexSubagentUsageItem(activity);
  if (usage) return { kind: "usage", activity, item: usage };
  const lifecycle = parseCodexSubagentLifecycleItem(activity);
  if (lifecycle) return { kind: "lifecycle", activity, item: lifecycle };
  const message = parseCodexSubagentMessageItem(activity);
  if (message) return { kind: "message", activity, item: message };
  const toolActivity = parseCodexSubagentToolActivityItem(activity);
  return toolActivity ? { kind: "toolActivity", activity, item: toolActivity } : null;
}

function trimProjectionEvents(
  events: NativeProjectionEvent[],
  limit: number,
  edge: "earliest" | "latest",
): void {
  if (events.length <= limit * 2) return;
  events.sort((left, right) =>
    compareOrchestrationActivitiesByOrder(left.activity, right.activity),
  );
  if (edge === "earliest") events.splice(limit);
  else events.splice(0, events.length - limit);
}

function statusFromNative(
  nativeStatus: string | undefined,
  tool: CodexCollabTool,
): CodexSubagentStatus {
  switch (nativeStatus) {
    case "pendingInit":
      return "pending";
    case "running":
      return "running";
    case "completed":
      return "completed";
    case "errored":
    case "notFound":
      return "failed";
    case "interrupted":
      return "interrupted";
    case "shutdown":
      return tool === "closeAgent" ? "cancelled" : "idle";
    default:
      return tool === "wait" ? "waiting" : "running";
  }
}

function toolLabel(tool: CodexCollabTool): string {
  switch (tool) {
    case "spawnAgent":
      return "Spawned";
    case "sendInput":
      return "Received input";
    case "resumeAgent":
      return "Resumed";
    case "wait":
      return "Waiting";
    case "closeAgent":
      return "Closed";
  }
}

function isLive(status: CodexSubagentStatus): boolean {
  return status === "pending" || status === "running" || status === "waiting";
}

function appendActivity(
  agent: MutableAgent,
  activity: CodexSubagentActivity,
  retainAllAvailableEvidence: boolean,
): void {
  const existingIndex = agent.activities.findIndex((entry) => entry.id === activity.id);
  if (existingIndex >= 0) agent.activities.splice(existingIndex, 1);
  agent.activities.push(activity);
  if (!retainAllAvailableEvidence && agent.activities.length > MAX_CODEX_SUBAGENT_ACTIVITIES) {
    agent.activities.splice(0, agent.activities.length - MAX_CODEX_SUBAGENT_ACTIVITIES);
  }
}

function appendMessage(
  agent: MutableAgent,
  message: CodexSubagentMessage,
  retainAllAvailableEvidence: boolean,
): void {
  const existingIndex = agent.messages.findIndex((entry) => entry.id === message.id);
  if (existingIndex >= 0) {
    agent.messages[existingIndex] = {
      ...message,
      createdAt: agent.messages[existingIndex]!.createdAt,
    };
  } else if (
    agent.messages.at(-1)?.role === message.role &&
    agent.messages.at(-1)?.text === message.text
  ) {
    return;
  } else {
    agent.messages.push(message);
  }
  if (!retainAllAvailableEvidence && agent.messages.length > MAX_CODEX_SUBAGENT_MESSAGES) {
    agent.messages.splice(0, agent.messages.length - MAX_CODEX_SUBAGENT_MESSAGES);
  }
}

/**
 * Projects native Codex collaboration items for one parent conversation.
 * Provider gating and activity scoping are explicit so Workspace Action Runs
 * and other providers can never enter this read model.
 */
export function deriveCodexSubagentProjection(
  provider: ProviderDriverKind | null | undefined,
  activities: ReadonlyArray<OrchestrationThreadActivity>,
  retention: "bounded" | "all-available" = "bounded",
): CodexSubagentProjection | null {
  if (provider !== "codex") return null;
  const retainAllAvailableEvidence = retention === "all-available";
  const sourceActivityLimit = retainAllAvailableEvidence
    ? Number.POSITIVE_INFINITY
    : MAX_CODEX_SUBAGENT_SOURCE_ACTIVITIES;
  const agentLimit = retainAllAvailableEvidence ? Number.POSITIVE_INFINITY : MAX_CODEX_SUBAGENTS;

  const recentNativeEvents: NativeProjectionEvent[] = [];
  const spawnSeeds: NativeProjectionEvent[] = [];
  for (const activity of activities) {
    const event = parseNativeProjectionEvent(activity);
    if (!event) continue;
    recentNativeEvents.push(event);
    trimProjectionEvents(recentNativeEvents, sourceActivityLimit, "latest");
    if (codexSubagentSpawnBatchId(activity.payload)) {
      spawnSeeds.push(event);
      trimProjectionEvents(spawnSeeds, agentLimit, "earliest");
    }
  }
  recentNativeEvents.sort((left, right) =>
    compareOrchestrationActivitiesByOrder(left.activity, right.activity),
  );
  spawnSeeds.sort((left, right) =>
    compareOrchestrationActivitiesByOrder(left.activity, right.activity),
  );
  const ordered = [
    ...new Map(
      [...spawnSeeds.slice(0, agentLimit), ...recentNativeEvents.slice(-sourceActivityLimit)].map(
        (event) => [event.activity.id, event],
      ),
    ).values(),
  ].toSorted((left, right) => compareOrchestrationActivitiesByOrder(left.activity, right.activity));
  if (!ordered.some((entry) => codexSubagentSpawnBatchId(entry.activity.payload))) {
    return null;
  }

  const agents = new Map<string, MutableAgent>();
  const observedStatusById = new Map<string, CodexSubagentStatus>();
  const batches = new Map<string, CodexSubagentSpawnBatch>();
  let truncated = false;

  const ensureAgent = (
    agentId: string,
    item: CodexCollabToolItem,
    activity: OrchestrationThreadActivity,
  ): MutableAgent | null => {
    const existing = agents.get(agentId);
    if (existing) return existing;
    if (agents.size >= agentLimit) {
      truncated = true;
      return null;
    }
    const agent: MutableAgent = {
      id: agentId,
      parentNativeThreadId: item.senderThreadId,
      spawnBatchId: item.id,
      spawnOrder: agents.size,
      status: "pending",
      identity: agentId,
      ...(item.prompt ? { role: item.prompt } : {}),
      ...(item.model ? { model: item.model } : {}),
      ...(item.reasoningEffort ? { effort: item.reasoningEffort } : {}),
      startedAt: activity.createdAt,
      messages: [],
      activities: [],
    };
    agents.set(agentId, agent);
    return agent;
  };

  for (const entry of ordered) {
    const { activity } = entry;
    if (entry.kind === "subagentActivity") {
      const { item } = entry;
      if (item.kind === "started") {
        observedStatusById.set(item.agentThreadId, "running");
      }
      let agent = agents.get(item.agentThreadId);
      if (item.kind === "started" && item.parentThreadId && !agent) {
        if (agents.size >= agentLimit) {
          truncated = true;
        } else {
          agent = {
            id: item.agentThreadId,
            parentNativeThreadId: item.parentThreadId,
            spawnBatchId: item.id,
            spawnOrder: agents.size,
            status: "running",
            identity: item.agentThreadId,
            role: item.agentPath,
            startedAt: activity.createdAt,
            messages: [],
            activities: [],
          };
          agents.set(agent.id, agent);
        }
      }
      if (!agent) continue;
      agent.currentActivity = `Subagent ${item.kind}`;
      if (item.kind === "started") {
        agent.status = "running";
        const existingBatch = batches.get(item.id);
        if (existingBatch) {
          if (!existingBatch.agentIds.includes(agent.id)) {
            batches.set(item.id, {
              ...existingBatch,
              agentIds: [...existingBatch.agentIds, agent.id],
            });
          }
        } else {
          batches.set(item.id, {
            id: item.id,
            createdAt: activity.createdAt,
            agentIds: [agent.id],
          });
        }
      } else if (item.kind === "interrupted") {
        agent.status = "interrupted";
        agent.completedAt = activity.createdAt;
        observedStatusById.set(agent.id, "interrupted");
      }
      appendActivity(
        agent,
        {
          id: `${entry.item.id}:${agent.id}`,
          createdAt: activity.createdAt,
          tool: "subagentActivity",
          label: `${item.kind} · ${item.agentPath}`,
        },
        retainAllAvailableEvidence,
      );
      continue;
    }
    if (entry.kind === "usage") {
      const agent = agents.get(entry.item.agentThreadId);
      if (agent) agent.usage = entry.item.usage;
      continue;
    }
    if (entry.kind === "lifecycle") {
      const agent = agents.get(entry.item.agentThreadId);
      if (!agent) continue;
      agent.status = entry.item.status;
      observedStatusById.set(agent.id, entry.item.status);
      agent.currentActivity = `Subagent ${entry.item.status}`;
      if (entry.item.error) agent.error = entry.item.error;
      if (isLive(entry.item.status) || entry.item.status === "idle") {
        delete agent.completedAt;
      } else {
        agent.completedAt = activity.createdAt;
      }
      continue;
    }
    if (entry.kind === "message") {
      const agent = agents.get(entry.item.agentThreadId);
      if (!agent) continue;
      agent.currentActivity = "Responded";
      if (entry.item.text) {
        agent.result = entry.item.text;
        appendMessage(
          agent,
          {
            id: `${activity.id}:${agent.id}:assistant`,
            createdAt: activity.createdAt,
            role: "assistant",
            text: entry.item.text,
          },
          retainAllAvailableEvidence,
        );
      }
      continue;
    }
    if (entry.kind === "toolActivity") {
      const agent = agents.get(entry.item.agentThreadId);
      if (!agent) continue;
      agent.currentActivity = entry.item.label;
      agent.lastTool = entry.item.tool;
      appendActivity(
        agent,
        {
          id: `${entry.item.id}:${agent.id}`,
          createdAt: activity.createdAt,
          tool: entry.item.tool,
          label: entry.item.label,
        },
        retainAllAvailableEvidence,
      );
      continue;
    }
    const { item } = entry;
    if (item.tool === "spawnAgent") {
      const retainedAgentIds: string[] = [];
      for (const agentId of item.receiverThreadIds) {
        if (ensureAgent(agentId, item, activity)) retainedAgentIds.push(agentId);
      }
      if (!batches.has(item.id) && retainedAgentIds.length > 0) {
        batches.set(item.id, {
          id: item.id,
          createdAt: activity.createdAt,
          agentIds: retainedAgentIds,
        });
      }
    }

    const targetIds = new Set([...item.receiverThreadIds, ...Object.keys(item.agentsStates)]);
    if (agents.has(item.senderThreadId) && item.tool === "wait") {
      targetIds.add(item.senderThreadId);
    }
    for (const agentId of targetIds) {
      const nativeStatus = item.agentsStates[agentId]?.status;
      const nextStatus =
        agentId === item.senderThreadId && item.tool === "wait"
          ? "waiting"
          : statusFromNative(nativeStatus, item.tool);
      observedStatusById.set(agentId, nextStatus);
      const agent = agents.get(agentId);
      if (!agent) continue;
      if ((item.tool === "spawnAgent" || item.tool === "sendInput") && item.prompt) {
        appendMessage(
          agent,
          {
            id: `${item.id}:${agentId}:user`,
            createdAt: activity.createdAt,
            role: "user",
            text: item.prompt,
          },
          retainAllAvailableEvidence,
        );
      }
      agent.status = nextStatus;
      agent.lastTool = item.tool;
      const nativeMessage = item.agentsStates[agentId]?.message;
      agent.currentActivity = nativeMessage ?? toolLabel(item.tool);
      if (item.model) agent.model = item.model;
      if (item.reasoningEffort) agent.effort = item.reasoningEffort;
      if (nativeMessage && agent.status === "completed") {
        agent.result = nativeMessage;
        appendMessage(
          agent,
          {
            id: `${item.id}:${agentId}:assistant`,
            createdAt: activity.createdAt,
            role: "assistant",
            text: nativeMessage,
          },
          retainAllAvailableEvidence,
        );
      }
      if (
        nativeMessage &&
        (agent.status === "failed" ||
          agent.status === "cancelled" ||
          agent.status === "interrupted")
      ) {
        agent.error = nativeMessage;
      }
      if (agent.status === "failed" && !agent.error) {
        agent.error = "Codex reported that this agent failed.";
      }
      if (
        agent.status === "completed" ||
        agent.status === "failed" ||
        agent.status === "cancelled" ||
        agent.status === "interrupted"
      ) {
        agent.completedAt = activity.createdAt;
      } else {
        delete agent.completedAt;
      }
      appendActivity(
        agent,
        {
          id: `${activity.id}:${agentId}`,
          createdAt: activity.createdAt,
          tool: item.tool,
          label: toolLabel(item.tool),
        },
        retainAllAvailableEvidence,
      );
    }
  }

  const projectedAgents = [...agents.values()].map<CodexSubagent>((agent) => {
    const parentId = agents.has(agent.parentNativeThreadId) ? agent.parentNativeThreadId : null;
    const { parentNativeThreadId: _parentNativeThreadId, ...rest } = agent;
    return { ...rest, parentId };
  });
  return {
    agents: projectedAgents,
    batches: [...batches.values()],
    liveCount: [...observedStatusById.values()].filter(isLive).length,
    totalCount: observedStatusById.size,
    truncated,
  };
}
